import React, { ReactNode, useContext } from 'react';
import {
  LiveStreamListingApi,
  StoreListingApi,
  StorePublicListingApi,
  UpcomingShowSendItemApi,
} from '../../generated/api';
import { CouponServiceHelper } from '../coupons/CouponServiceHelper';
import { LinkServiceHelper } from '../dynamic-links/LinkServiceHelper';
import { GiveawayServiceHelper } from '../giveaway/GiveawayServiceHelper';
import { UserServiceHelper } from '../hooks/UserServiceHelper';
import { ListingFirestoreHelper } from '../listing/ListingFirestoreHelper';
import { SendItemServiceHelper } from '../order/SendItemServiceHelper';
import { ReferralServiceHelper } from '../user/ReferralServiceHelper';
import analyticsService from './analytics.service';
import appSellerApplicationService from './appSellerApplication.service';
import auctionService from './auction.service';
import categoriesService from './categories.service';
import commonService from './common.service';
import configuration from './configuration';
import couponService from './coupon.service';
import defaultService from './default.service';
import deleteAccountService from './deleteAccount.service';
import { discoveryService } from './discovery.service';
import featuredTagService from './featuredTag.service';
import homeFeedService from './homeFeed.service';
import kustomerService from './kustomer.service';
import linkService from './link.service';
import { liveStreamGiveawayService } from './liveStreamGiveaway.service';
import livestreamsService from './livestreams.service';
import { liveStreamSendItemService } from './liveStreamSendItem.service';
import messageService from './message.service';
import oauth2Service from './oauth2.service';
import onboardingPushService from './onboardingPush.service';
import orderService from './order.service';
import paymentService from './payment.service';
import pickupAddressService from './pickup-address.service';
import postService from './post.service';
import privateProfileService from './privateProfile.service';
import productCategoryService from './product-category.service';
import productCollectionService from './product-collection.service';
import referralService from './referral.service';
import { referralGiftService } from './referralGift.service';
import saasSubscriptionService from './saas-subscription.service';
import searchService from './search.service';
import seriesService from './series.service';
import shopifyService from './shopify.service';
import showEventService from './showEvent.service';
import showTimerService from './showTimer.service';
import storeService from './store.service';
import storeBadgeService from './storeBadge.service';
import storeListingService from './storeListing.service';
import storePublicListingService from './storePublicListing.service';
import themeTemplateService from './theme-template.service';
import tippingOrder from './tippingOrder.service';
import { upcomingShowService } from './upcomingShow.service';
import { upcomingShowGiveawayService } from './upcomingShowGiveaway.service';
import userService from './user.service';
import withdrawTransactionService from './withdrawTransaction.service';
import zendeskService from './zendesk.service';

const listingFirestoreHelper = new ListingFirestoreHelper(
  new LiveStreamListingApi(configuration),
  new StorePublicListingApi(configuration),
  new StoreListingApi(configuration),
);

const services = {
  defaultService,
  analyticsService,
  appSellerApplicationService,
  auctionService,
  storeService,
  categoriesService,
  couponService,
  referralService,
  couponServiceHelper: new CouponServiceHelper(couponService),
  livestreamsService,
  upcomingShowService,
  deleteAccountService,
  linkService,
  linkServiceHelper: new LinkServiceHelper(messageService, linkService),
  pickupAddressService,
  orderService,
  commonService,
  userService,
  userServiceHelper: new UserServiceHelper(userService),
  searchService,
  withdrawTransactionService,
  listingFirestoreHelper,
  kustomerService,
  zendeskService,
  referralServiceHelper: new ReferralServiceHelper(searchService, userService),
  discoveryService,
  showEventService,
  shopifyService,
  paymentService,
  privateProfileService,
  messageService,
  referralGiftService,
  saasSubscriptionService,
  storeBadgeService,
  showTimerService,
  liveStreamGiveawayService,
  upcomingShowGiveawayService,
  giveawayServiceHelper: new GiveawayServiceHelper(
    listingFirestoreHelper,
    liveStreamGiveawayService,
    upcomingShowGiveawayService,
  ),
  liveStreamSendItemService,
  sendItemServiceHelper: new SendItemServiceHelper(
    liveStreamSendItemService,
    new UpcomingShowSendItemApi(configuration),
  ),
  tippingOrder,
  featuredTagService,
  homeFeedService,
  onboardingPushService,
  storeListingService,
  storePublicListingService,
  productCategoryService,
  productCollectionService,
  seriesService,
  oauth2Service,
  themeTemplateService,
  postService,
};

const ServiceFactoryContext = React.createContext<ServiceFactory>(null as any);

export type ServiceFactory = typeof services;

export const useServiceFactory = (): ServiceFactory =>
  useContext(ServiceFactoryContext);

export const ServiceFactoryProvider = (props: {
  services?: typeof services;
  children: ReactNode;
}) => {
  return (
    <ServiceFactoryContext.Provider value={props.services ?? services}>
      {props.children}
    </ServiceFactoryContext.Provider>
  );
};
